import type { KeptMechanism, PremiumFlow, PremiumFlowContributor, PremiumFlowFate } from './types/index.js';
import { classifyPositionEvent } from './rollClassification.js';

export interface FlowLegTxn {
  transactionType: string;
  quantity: number;
  pricePerContract: number;
}
export interface FlowLeg {
  action?: string; // opening action: 'STO'|'BTO' (or 'STC'|'BTC'); absent → treated as short
  quantity: number;
  quantityRemaining: number;
  pricePerContract: number; // opening fill; required for per-leg credit (short +, long -)
  closeReason?: string | null; // leg-level: 'expired'|'assigned'|'exercised'|'closed'|null
  transactions: FlowLegTxn[];
}
export interface FlowPosition {
  chainId: string;
  symbol: string;
  netPremium: number;
  status: string;
  closeReason: string | null;
  // True when this position's active close was absorbed by a roll (its
  // successor classifies as 'rolled'). closeReason alone can't express this:
  // closeExpiredPositions stamps 'expired' when the last legs to die expired,
  // even if the other side was rolled — see markRollClosures.
  closedByRoll?: boolean;
  legs: FlowLeg[];
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export interface RollMarkPosition {
  id: string;
  openedAt: Date | string; // API JSON delivers ISO strings; server code passes Dates
  rollNumber: number;
  closeReason?: string | null;
  legs: { closeReason: string | null }[];
}

const openedAtMs = (p: RollMarkPosition): number =>
  p.openedAt instanceof Date ? p.openedAt.getTime() : new Date(p.openedAt).getTime();

/**
 * Ids of positions (within ONE chain) whose active close was absorbed by a
 * successor roll. Same walk order and close-evidence classification as the
 * journal's deriveChainEvents, so the Sankey's Roll-cost band and the journal
 * narrative agree on what a roll is.
 */
export function markRollClosures(positions: RollMarkPosition[]): Set<string> {
  const sorted = [...positions].sort(
    (a, b) => openedAtMs(a) - openedAtMs(b) || a.rollNumber - b.rollNumber || a.id.localeCompare(b.id),
  );
  const closedByRoll = new Set<string>();
  for (let i = 1; i < sorted.length; i++) {
    if (classifyPositionEvent(sorted[i], sorted[i - 1]) === 'rolled') closedByRoll.add(sorted[i - 1].id);
  }
  return closedByRoll;
}

// Terminal events where the option writer keeps the credit; their 'close' leg
// transactions are expiration/assignment/exercise bookkeeping, not a debit paid.
const TERMINAL_KEPT = new Set(['expired', 'assigned', 'exercised']);

const isLongAction = (action: string | undefined): boolean => action === 'BTO' || action === 'BTC';

// Signed opening credit for one leg: short (STO/STC, or action absent) is a
// credit received (+); long (BTO/BTC) is a debit paid (-). Guarded against
// quantity <= 0 (bad data) to avoid a div-by-zero in the open/resolved split.
const legCredit = (l: FlowLeg): number => {
  if (l.quantity <= 0) return 0;
  const signed = l.pricePerContract * l.quantity * 100;
  return isLongAction(l.action) ? -signed : signed;
};

// Position-level mechanism attribution used before this change; kept as the
// defensive fallback shape for a terminal leg's closeReason (which is always
// expired|assigned|exercised by construction — the third branch never fires
// in practice, mirrored here for readability/symmetry with keptMechanismForLeg).
const keptMechanismForLeg = (closeReason: string | null | undefined): KeptMechanism =>
  closeReason === 'expired' ? 'expired'
    : closeReason === 'assigned' || closeReason === 'exercised' ? 'assigned'
    : 'boughtBack';

// Distributes a fate-group's post-nudge total back across its member legs,
// proportional to each leg's raw resolvedPortion. If the group's raw total
// was exactly zero but the O-nudge still moved it away from zero (rare: legs
// summed to zero, stored netPremium didn't), the whole nudged total lands on
// the single largest-magnitude leg (Decision 7 fallback) instead of being
// silently dropped from the breakdown.
function scaleLegPortions(rawTotal: number, nudgedTotal: number, portions: number[]): number[] {
  if (rawTotal !== 0) {
    const scale = nudgedTotal / rawTotal;
    return portions.map((p) => p * scale);
  }
  if (portions.length === 0) return [];
  let targetIdx = 0;
  for (let i = 1; i < portions.length; i++) {
    if (Math.abs(portions[i]) > Math.abs(portions[targetIdx])) targetIdx = i;
  }
  return portions.map((_, i) => (i === targetIdx ? nudgedTotal : 0));
}

type NudgeComponent = 'closed' | 'terminal' | 'inPlay';

// Tie-break when two components share the largest magnitude: closed, then
// terminal, then inPlay (locked for determinism — see design review Decision 2).
function nudgeTarget(inPlay0: number, terminal0: number, closed0: number): NudgeComponent {
  const a = { inPlay: Math.abs(inPlay0), terminal: Math.abs(terminal0), closed: Math.abs(closed0) };
  if (a.closed >= a.terminal && a.closed >= a.inPlay) return 'closed';
  if (a.terminal >= a.inPlay) return 'terminal';
  return 'inPlay';
}

/**
 * Traces each position's opening net credit `O = max(0, netPremium)` to
 * fates, resolved PER LEG FATE-GROUP so a mixed position (e.g. puts expired,
 * calls rolled) can legitimately contribute to more than one fate — the
 * puts' own credit is Kept even though the calls' roll cost exceeds the
 * whole position's net credit.
 *
 * Per position:
 *  1. Partition legs: signed legCredit (short +, long -) split by remaining
 *     quantity into openPortion / resolvedPortion.
 *  2. Group resolved legs: terminal-kept (closeReason expired/assigned/
 *     exercised) vs active-close (closeReason 'closed', or a close txn, or
 *     quantityRemaining===0 with neither — degenerate data, treated as a
 *     zero-cost close, same as today). Terminal legs keep their credit
 *     outright; active-close legs' resolvedPortion nets against a
 *     direction-aware closeCost summed over exactly that group (short pays
 *     to close, long's sale proceeds offset rather than add).
 *  3. inPlay0 = max(0, Σ openPortion) — clamped before the nudge (asymmetric
 *     remaining quantity across long/short legs can otherwise go negative).
 *  4. O-nudge: the three raw components (inPlay0, terminal0, closed0) rarely
 *     sum to exactly O — stored netPremium and leg fill prices can drift a
 *     few dollars — so the largest-magnitude of the three absorbs the
 *     difference before fate math runs, keeping downstream bands and
 *     contributors internally consistent without leaning on the final
 *     $0.01-style global drift to cover a multi-dollar hole.
 *  5. Fate math runs on the NUDGED triple: keptTerminal = max(0, terminal),
 *     returned = clamp(closeCost, 0, max(0, closed)) (Decision A, now
 *     group-scoped), keptClosed = max(0, closed) - returned, kept =
 *     keptTerminal + keptClosed. returned buckets to rollCost when
 *     closedByRoll || closeReason==='rolled', else closedForCost.
 *  6. netRealizedPnl / lossBeyondPremium use the RAW (pre-nudge) terminal0 /
 *     closed0 plus the true closeCost — P&L must not be contaminated by the
 *     band-reconciliation nudge, or it stops being the position's true
 *     realized economics.
 *  7. keptBreakdown / contributors attribute by leg evidence: each terminal
 *     leg's nudge-scaled resolvedPortion goes to its own closeReason
 *     mechanism (expired/assigned); keptClosed goes to boughtBack, whole.
 *  8. Global band drift and Kept-mechanism drift (existing, now with an
 *     explicit floor-at-0 per mechanism) run once at the end across all
 *     positions, restoring collected = Σ bands exactly.
 */
export function computePremiumFlow(input: { positions: FlowPosition[] }): PremiumFlow {
  let collected = 0, kept = 0, inPlay = 0, closedForCost = 0, rollCost = 0;
  let netRealizedPnl = 0, lossBeyondPremium = 0;
  const keptBreakdown: Record<KeptMechanism, number> = { expired: 0, assigned: 0, boughtBack: 0 };
  const contributors: PremiumFlowContributor[] = [];

  const addContributor = (fate: PremiumFlowFate, p: FlowPosition, amount: number, keptBy?: KeptMechanism) => {
    if (amount > 0.005) contributors.push({ fate, chainId: p.chainId, symbol: p.symbol, amount: round2(amount), ...(keptBy ? { keptBy } : {}) });
  };

  for (const p of input.positions) {
    const O = Math.max(0, p.netPremium);
    if (O === 0) continue;
    collected += O;

    let rawInPlay = 0, terminal0 = 0, closed0 = 0, closeCost = 0;
    const terminalLegs: { portion: number; closeReason: string | null | undefined }[] = [];

    for (const l of p.legs) {
      const credit = legCredit(l);
      const openFraction = l.quantity > 0 ? l.quantityRemaining / l.quantity : 0;
      const openPortion = credit * openFraction;
      const resolvedPortion = credit - openPortion;
      rawInPlay += openPortion;

      const isTerminal = l.closeReason != null && TERMINAL_KEPT.has(l.closeReason);
      const hasCloseTxn = l.transactions.some((t) => t.transactionType === 'close');
      const isActiveClose = !isTerminal && (l.closeReason === 'closed' || hasCloseTxn || l.quantityRemaining === 0);

      if (isTerminal) {
        terminal0 += resolvedPortion;
        terminalLegs.push({ portion: resolvedPortion, closeReason: l.closeReason });
      } else if (isActiveClose) {
        closed0 += resolvedPortion;
        const closeValue = l.transactions
          .filter((t) => t.transactionType === 'close')
          .reduce((s, t) => s + t.pricePerContract * t.quantity * 100, 0);
        // Short legs pay to close (debit); long legs are sold to close, and
        // those proceeds offset the debit rather than adding to it.
        closeCost += isLongAction(l.action) ? -closeValue : closeValue;
      }
      // Still fully open, no close evidence: contributes only via openPortion.
    }

    const inPlay0 = Math.max(0, rawInPlay); // Decision 5: clamp BEFORE the nudge.

    const target = nudgeTarget(inPlay0, terminal0, closed0);
    const nudgeAmount = O - (inPlay0 + terminal0 + closed0);
    const inPlay1 = inPlay0 + (target === 'inPlay' ? nudgeAmount : 0);
    const terminal1 = terminal0 + (target === 'terminal' ? nudgeAmount : 0);
    const closed1 = closed0 + (target === 'closed' ? nudgeAmount : 0);

    // Clamp inPlay to [0, O] immediately before the fate math: without this,
    // open short legs can carry inPlay1 above O when a resolved group in the
    // same position went negative (the nudge only zeroes THAT group, it
    // doesn't shrink inPlay1). An unclamped inPlay1 over-allocates past O the
    // same way the uncapped keptRaw did below — and leaks the excess into the
    // GLOBAL drift, which then steals it from an unrelated position's band
    // (see finding: Position A's open legs must not eat into Position B's
    // Kept). Used for the inPlay band, its contributor, and keptCap.
    const inPlayShare = Math.min(Math.max(0, inPlay1), O);

    // Fate math on the nudged triple.
    const keptTerminal = Math.max(0, terminal1);
    const returned = Math.min(Math.max(closeCost, 0), Math.max(0, closed1));
    const keptClosed = Math.max(0, closed1) - returned;
    const keptRaw = keptTerminal + keptClosed;

    // Per-position cap: when one fate group's nudged credit is negative, it
    // contributes nothing to any band, so local bands can sum above O
    // (keptRaw over-allocates). Capping HERE keeps the invariant that a
    // position's own bands sum to its own O, so the later GLOBAL drift only
    // ever has to correct pennies — never siphon a multi-dollar excess out of
    // an unrelated position's band (see finding: Position A's over-allocated
    // Kept must not eat into Position B's In-play). Paired with the inPlay
    // clamp above, these two guards together restore the full per-position
    // band-sum invariant (kept + inPlay + closedForCost/rollCost == O),
    // not just the Kept-specific piece either guard covers alone.
    const keptCap = Math.max(0, O - inPlayShare - returned);
    const keptShare = Math.min(keptRaw, keptCap);

    inPlay += inPlayShare;
    kept += keptShare;

    // P&L from RAW pre-nudge group credits (Decision 3) — never nudge-contaminated.
    netRealizedPnl += terminal0 + (closed0 - closeCost);
    lossBeyondPremium += Math.max(0, -terminal0) + Math.max(0, closeCost - Math.max(0, closed0));

    // keptBreakdown + kept contributors: nudge-scale the terminal group's
    // per-leg resolved portions and attribute by leg closeReason; keptClosed
    // (already net of any active-close nudge) goes to boughtBack whole.
    // Same cap, applied to the mechanism breakdown: when keptShare < keptRaw
    // (the per-position cap bound), scale every kept-mechanism attribution
    // down by the same factor so keptBreakdown still sums to keptShare
    // instead of the pre-cap keptRaw.
    const capScale = keptRaw > 0 ? keptShare / keptRaw : 0;
    const positionMechanisms: Record<KeptMechanism, number> = { expired: 0, assigned: 0, boughtBack: 0 };
    if (terminalLegs.length > 0) {
      const scaled = scaleLegPortions(terminal0, keptTerminal, terminalLegs.map((t) => t.portion));
      terminalLegs.forEach((t, i) => {
        positionMechanisms[keptMechanismForLeg(t.closeReason)] += scaled[i] * capScale;
      });
    }
    if (keptClosed !== 0) positionMechanisms.boughtBack += keptClosed * capScale;

    (Object.keys(positionMechanisms) as KeptMechanism[]).forEach((mech) => {
      keptBreakdown[mech] += positionMechanisms[mech];
      addContributor('kept', p, positionMechanisms[mech], mech);
    });

    addContributor('inPlay', p, inPlayShare);
    if (p.closedByRoll || p.closeReason === 'rolled') {
      rollCost += returned;
      addContributor('rollCost', p, returned);
    } else {
      closedForCost += returned;
      addContributor('closedForCost', p, returned);
    }
  }

  // Rounding reconciliation: nudge the largest band so the four sum to collected.
  collected = round2(collected);
  const bands: Record<PremiumFlowFate, number> = {
    kept: round2(kept), inPlay: round2(inPlay), closedForCost: round2(closedForCost), rollCost: round2(rollCost),
  };
  const bandSum = bands.kept + bands.inPlay + bands.closedForCost + bands.rollCost;
  const drift = round2(collected - bandSum);
  if (drift !== 0) {
    const largest = (Object.keys(bands) as PremiumFlowFate[]).reduce((a, b) => (bands[a] >= bands[b] ? a : b));
    bands[largest] = round2(bands[largest] + drift);
  }

  // Kept-mechanism floor (Decision 6 — leg-level attribution can sum negative
  // before this floor in a way position-level attribution never did) + the
  // existing drift so the three mechanisms sum to the (possibly globally
  // drift-adjusted) Kept band.
  const mechanisms: Record<KeptMechanism, number> = {
    expired: Math.max(0, round2(keptBreakdown.expired)),
    assigned: Math.max(0, round2(keptBreakdown.assigned)),
    boughtBack: Math.max(0, round2(keptBreakdown.boughtBack)),
  };
  const mechDrift = round2(bands.kept - (mechanisms.expired + mechanisms.assigned + mechanisms.boughtBack));
  if (mechDrift !== 0) {
    const largestMech = (Object.keys(mechanisms) as KeptMechanism[]).reduce((a, b) => (mechanisms[a] >= mechanisms[b] ? a : b));
    mechanisms[largestMech] = round2(mechanisms[largestMech] + mechDrift);
  }

  return {
    collected,
    kept: bands.kept,
    inPlay: bands.inPlay,
    closedForCost: bands.closedForCost,
    rollCost: bands.rollCost,
    keptBreakdown: mechanisms,
    netRealizedPnl: round2(netRealizedPnl),
    lossBeyondPremium: round2(lossBeyondPremium),
    contributors,
  };
}
